import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Text, Spinner, Flex, Badge, Separator } from '@radix-ui/themes'
import { ScrollablePageLayout } from '~/components/ScrollablePageLayout'
import { EmptyState } from '~/components/EmptyState'
import { RichCalloutCard } from '~/components/RichCalloutCard'
import { getIncidentWithUpdates } from '~/features/incidents/service'
import { DateTime } from 'luxon'
import HumanTimestamp from '~/components/HumanTimestamp'
import { LuTriangleAlert, LuCircleCheck } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import MarkdownRenderer from '~/components/MarkdownRenderer'
import { SectionCard } from '~/components/SectionCard'
import { PropsWithChildren } from 'react'
import { ThemeColor } from '~/theme-utils'
import { NotificationLevel } from '~/features/notifications/service'
import { useCurrentLanguage } from '~/hooks/useCurrentLanguage'

export const Route = createFileRoute('/sites/$siteID/incidents/$incidentID')({
  component: RouteComponent
})

function RouteComponent() {
  const { incidentID } = Route.useParams()
  const locale = useCurrentLanguage()
  const { t } = useTranslation('sites')

  const {
    data: incident,
    isLoading,
    error
  } = useQuery({
    queryKey: ['incident', incidentID],
    queryFn: () => getIncidentWithUpdates(incidentID)
  })

  if (isLoading) {
    return (
      <ScrollablePageLayout title={t('incidents.detail.pageTitle')}>
        <Flex align="center" justify="center" py="8">
          <Spinner size="3" />
        </Flex>
      </ScrollablePageLayout>
    )
  }

  if (error || !incident) {
    return (
      <ScrollablePageLayout title={t('incidents.detail.pageTitle')}>
        <EmptyState
          title={t('incidents.detail.notFound.title')}
          description={t('incidents.detail.notFound.description')}
        />
      </ScrollablePageLayout>
    )
  }

  const isActive = incident.status === 'active'

  const lastIncidentNotification = incident.notifications[0]

  return (
    <ScrollablePageLayout title={t('incidents.detail.pageTitle')}>
      <Flex direction="column" gap="5">
        <Flex direction="column" gap="4">
          {/* Incident Summary Section */}
          <RichCalloutCard
            color={isActive ? 'amber' : 'green'}
            title={incident.title}
            subtitle={`${t('incidents.detail.openedSince')} ${DateTime.fromISO(incident.created_at).toLocaleString(
              DateTime.DATETIME_MED_WITH_WEEKDAY,
              { locale }
            )}`}
            icon={
              isActive ? (
                <LuTriangleAlert size={35} color="var(--amber-11)" />
              ) : (
                <LuCircleCheck size={35} color="var(--green-9)" />
              )
            }
            actions={
              <Flex align="center" gap="2">
                <Badge variant="soft" size="1" color="gray">
                  {t(`incidents.status.types.${incident.type}`)}
                </Badge>
                <Badge color={isActive ? 'amber' : 'green'} variant="soft" size="2">
                  {t(`incidents.status.statuses.${incident.status}`)}
                </Badge>
              </Flex>
            }
          >
            {lastIncidentNotification ? (
              <Flex direction={'column'} gap="2" className="grow">
                <Flex align={'center'} justify={'between'} className="mb-2">
                  <Text className="line-clamp-1" size={'3'} weight={'medium'}>
                    {lastIncidentNotification.title}
                  </Text>
                  <HumanTimestamp
                    timestamp={DateTime.fromISO(lastIncidentNotification.created_at)}
                    size={'1'}
                    className="ml-auto text-right min-w-[75px]"
                    color="gray"
                  />
                </Flex>
                <Text size="2" color="gray">
                  <MarkdownRenderer content={lastIncidentNotification.message_body ?? ''} />
                </Text>
              </Flex>
            ) : (
              <Text size="2" color="gray">
                {t('incidents.detail.noRecentUpdates')}
              </Text>
            )}
          </RichCalloutCard>
        </Flex>
        <SectionCard title={t('incidents.detail.updatesSection.title')}>
          <Flex direction="column" gap={'1'}>
            <Separator size="4" my={'3'} />
            {incident.notifications.map((notification, idx) => (
              <TimelineItem
                key={notification.id}
                isOldest={idx === incident.notifications.length - 1}
                color={
                  notification.level === 'critical'
                    ? 'red'
                    : notification.level === 'warning'
                      ? 'amber'
                      : (['good', 'amazing'] as NotificationLevel[]).includes(
                            notification.level as NotificationLevel
                          )
                        ? 'green'
                        : 'gray'
                }
              >
                <Flex direction={'column'} gap="2" className="grow" pl={'2'} pb={'5'}>
                  <Flex align={'center'} justify={'between'} className="mb-2">
                    <Text className="line-clamp-1" size={'3'} weight={'medium'}>
                      {notification.title}
                    </Text>
                    <HumanTimestamp
                      timestamp={DateTime.fromISO(notification.created_at)}
                      size={'1'}
                      className="ml-auto text-right min-w-[75px]"
                      color="gray"
                    />
                  </Flex>
                  <Text size="2" color="gray">
                    <MarkdownRenderer content={notification.message_body ?? ''} />
                  </Text>
                </Flex>
              </TimelineItem>
            ))}
          </Flex>
        </SectionCard>
      </Flex>
    </ScrollablePageLayout>
  )
}

type TimelineItemProps = PropsWithChildren<{
  isOldest: boolean
  color: ThemeColor
}>
const TimelineItem = ({ children, isOldest, color }: TimelineItemProps) => {
  return (
    <Flex gap={'2'}>
      <div className="flex flex-col items-center w-4 flex-none">
        <div
          className="w-2 h-2 mt-2 rounded-full flex-none"
          style={{ backgroundColor: `var(--${color}-9)` }}
        ></div>
        {!isOldest && <div className="w-px grow mt-[7px]" style={{ backgroundColor: `var(--${color}-9)` }}></div>}
      </div>
      {children}
    </Flex>
  )
}
